import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { capitalService } from "../services/service";
import { useAuth } from "./useAuth";

const EMPTY_PARAMS = {};
const CAPITALS_KEY = ["capitals"];

export const useShopCapitals = (shopId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const { hasPermission } = useAuth();

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const canRead = hasPermission([
        "manage_all",
        "manage_shop_modules",
        "manage_capital",
        "read_capital",
    ]);

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        search: searchTerm,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [currentPage, pageSize, searchTerm, statusFilter, startDate, endDate, initialParams]);

    const query = useQuery({
        queryKey: [...CAPITALS_KEY, "shop", shopId, params],
        queryFn: () => capitalService.getAllByShop(shopId, params),
        enabled: Boolean(shopId) && canRead,
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: CAPITALS_KEY }),
        [queryClient]
    );

    const createCapital = useCallback(async (data) => {
        const response = await capitalService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const updateCapital = useCallback(async (id, data) => {
        const response = await capitalService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleCapitalStatus = useCallback(async (id) => {
        const response = await capitalService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    return {
        capitals: query.data?.data || [],
        pagination: query.data || null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        refresh: query.refetch,
        createCapital,
        updateCapital,
        toggleCapitalStatus,
    };
};

export const useCapital = useShopCapitals;
export const useCapitals = useShopCapitals;